import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ShieldCheck, MessageSquare, Clock, Heart, Truck } from "lucide-react";
import { images, company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";
import { Reveal, Overline } from "@/components/Reveal";
import { FinalCTA } from "@/components/FinalCTA";

const valueIcons = [ShieldCheck, MessageSquare, Clock, Heart, Truck];

export default function Azienda() {
  const { t, lang } = useLang();
  const ap = t.aboutPage;

  return (
    <>
      <Seo
        title={lang === "en" ? "Company & Fleet | EMME SERVICE CAR" : "Azienda e Flotta | EMME SERVICE CAR"}
        description={ap.title}
        path="/azienda"
      />

      <section className="relative overflow-hidden bg-carbon pb-16 pt-32">
        <div className="absolute inset-0 z-0">
          <img src={images.truckReal1} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/90 to-carbon/70" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs text-steel">
            <Link to="/" className="hover:text-amber">{t.breadcrumbHome}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-ice">{t.nav.company}</span>
          </nav>
          <Overline>{ap.overline}</Overline>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight text-ice sm:text-5xl">{ap.title}</h1>
        </div>
      </section>

      <section className="bg-carbon py-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-ice">{ap.historyTitle}</h2>
            <p className="mt-4 text-sm leading-relaxed text-steel">{ap.historyText}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-2xl font-bold text-ice">{ap.methodTitle}</h2>
            <p className="mt-4 text-sm leading-relaxed text-steel">{ap.methodText}</p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-edge bg-asphalt py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal><h2 className="font-display text-2xl font-bold text-ice">{ap.valuesTitle}</h2></Reveal>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-5">
            {ap.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={v} delay={i * 0.06}>
                  <div className="rounded-xl border border-edge bg-carbon p-5 text-center">
                    <Icon className="mx-auto h-6 w-6 text-amber" />
                    <p className="mt-3 font-display text-sm font-bold text-ice">{v}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet & people photos */}
      <section className="bg-carbon py-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 md:grid-cols-3 lg:px-8">
          {[images.truckReal1, images.operator, images.human].map((src, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="relative h-72 overflow-hidden rounded-xl border border-edge">
                <img src={src} alt={lang === "en" ? "EMME SERVICE CAR fleet and team" : "Flotta e personale EMME SERVICE CAR"} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute bottom-3 left-3 rounded bg-carbon/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-steel backdrop-blur-sm">
                  {lang === "en" ? "Placeholder — replace with real photo" : "Placeholder — sostituire con foto reale"}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Company data placeholders */}
      <section className="border-t border-edge bg-asphalt py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal><h2 className="font-display text-2xl font-bold text-ice">{ap.dataTitle}</h2></Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ap.placeholders.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.05}>
                <div className="rounded-xl border border-dashed border-edge bg-carbon p-5">
                  <p className="text-xs font-bold uppercase tracking-wide text-amber">{p.k}</p>
                  <p className="mt-2 font-display text-lg text-steel">{p.v}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="rounded-xl border border-edge bg-carbon p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-amber">{lang === "en" ? "Service area" : "Area operativa"}</p>
                <p className="mt-2 font-display text-lg text-ice">{company.areaFull}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
